import { Grid, Card, CardHeader, Box, CardContent, TextField, ListItem, Tooltip, IconButton, Stack, List, Typography, SxProps, Theme, FormGroup, 
  FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, FormHelperText, Switch,
  Alert,
  Autocomplete} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ClearIcon from "@mui/icons-material/Clear";
import InfoIcon from "@mui/icons-material/Info";
import ReplayIcon from "@mui/icons-material/Replay";
import { useRecordContext } from "ra-core";
import { FC, ReactElement, useEffect, useState } from "react";
import * as Yup from 'yup';
import { DetectPattern, ExclusionRule, InfoTypeInput, InfoTypeResponseDto, KeywordRule } from "~/gen/api/securefile";
import { usePlainStateFormUtils } from "~/components/input/usePlainStateFormUtils";
import { useSubdataAssembleController, SubdataAssembleContextProvider, useSubdataAssembleContext, AssembleIssue } from "~/components/input/useSubdataAssembleController";
import StringArrayTextField from "~/components/input/StringArrayTextField";
import { promiseWithResolvers } from "~/utils/promiseWithResolvers";
import { FileInfoTypeDataContextProvider, useFileInfoTypeDataContext } from "./FileInfoTypeDataProvider";


const baseInfoTypeRes = 'secure-files-base-info-type'


const topSchema = Yup.object({
  name: Yup.string().required('Name is required').max(100, 'Name must be at most 100 characters'),
  displayName: Yup.string().required('Display Name is required').max(100, 'Display Name must be at most 100 characters'),
  description: Yup.string().max(512, 'Description must be at most 512 characters'),
})

const InfoTypeEditor : FC<{ title: string}> = ({ title }) => {
  const record = useRecordContext<InfoTypeResponseDto>() ?? emptyInfoTypeDto
  // const { data: baseInfoTypeData, } = useGetMainList(baseInfoTypeRes, pages(), sorts(), {}, { action: 'CUSTOM_DATA_QUERY' })
  const createMode = record === emptyInfoTypeDto
  const isBuiltIn = record.isBuiltIn
  const initialVal = (record: InfoTypeResponseDto) => ({ 
    prevData: record,
    stage: {
      name: record.name,
      displayName: record.displayName,
      description: record.description,
      isEnabled: record.isEnabled,
    },
    denyListCount: record.denyList?.length ?? 0,
    detectPatternCount: record.detectPatterns?.length ?? 0,
    countTouched: false,
    errors: {} as any,
    touched: {} as any,
  })
  const [states, setStates] = useState(() => initialVal(record))
  useEffect(() => { setStates(v => initialVal(record)) }, [record])
  const { validate, fieldProps } = usePlainStateFormUtils(states, setStates, topSchema)
  const { registerAssembleFn } = useSubdataAssembleContext<{d:InfoTypeInput}>()
  const { assemble, contextValue } = useSubdataAssembleController<InfoTypeInput>({ 
    getInformed: (key, value) => {
      if (key === 'denyList') { setStates(v => ({...v, denyListCount: (value as []).length, countTouched: true}))}
      else if (key === 'detectPatterns') { setStates(v => ({...v, detectPatternCount: (value as []).length, countTouched: true}))}
      // the rest are ignored
    }
  })

  useEffect(() => {
    return registerAssembleFn('d', () => new Promise(async (resolve) => {
      const qV1 = promiseWithResolvers<typeof states>()
      setStates(v => {
        const errors = validate(v.stage) ?? {}
        let ks = Object.keys(v.stage)
        const v1 = {...v, errors, touched: ks.reduce((acc, k) => ({...acc, [k]:true}), {} as any)}
        qV1.resolve(v1)
        return v1
      })
      const v1 = await qV1.promise
      const ks = Object.keys(v1.errors)
      if (ks.length > 0) {
        resolve(new AssembleIssue(''))
        return
      }
      const result = await assemble()
      if (result instanceof AssembleIssue) {
        resolve(result)
        return
      } 
      const payload = {...result, ...v1.stage}
      const qV2 = promiseWithResolvers<typeof states>()
      setStates(v => {
        const v2 = {...v, denyListCount: payload.denyList?.length ?? 0, detectPatternCount: payload.detectPatterns?.length ?? 0, countTouched: true}
        qV2.resolve(v2)
        return v2
      })
      const v2 = await qV2.promise
      if (!isBuiltIn && (v2.denyListCount + v2.detectPatternCount < 1)) {
        resolve(new AssembleIssue(''))
        return
      }
      resolve(payload)
    }))
  }, [registerAssembleFn, assemble, validate])

  return (
    <Card>
      <CardHeader title={title} />
      <SubdataAssembleContextProvider value={contextValue}>
        <FileInfoTypeDataContextProvider>
          <CardContent>
            <Grid container spacing={4}>
              <Grid item xs={12}>
                <FormGroup sx={{ mb: 1 }}>
                  <FormControlLabel 
                    label="Enable Info Type" 
                    control={
                      <Switch checked={states.stage.isEnabled} 
                        onChange={(e, checked) => setStates(v => ({...v, stage: {...v.stage, isEnabled: checked},
                          touched: {...v.touched, isEnabled: true },
                        }))} />
                    } 
                  />
                </FormGroup>
              </Grid>
              <Grid item xs={6}>
                <TextField label="Name*" disabled={!createMode || isBuiltIn} multiline fullWidth {...fieldProps('name')}/>
              </Grid>
              <Grid item xs={6}>
                <TextField label="Display Name*" disabled={isBuiltIn} fullWidth multiline {...fieldProps('displayName')}/>
              </Grid>
              <Grid item xs={12}>
                <TextField label="Description" disabled={isBuiltIn} fullWidth multiline {...fieldProps('description')}/>
              </Grid>
              {!isBuiltIn && states.countTouched && (states.denyListCount + states.detectPatternCount < 1) &&
                <Grid item xs={12}>
                  <FormHelperText error>Should define at least 1 rule in either Keyword Detection or Pattern Detection</FormHelperText>
                </Grid>}
              {!isBuiltIn &&
                <>
                  <Grid item xs={12}>
                    <DenyListEditor prevData={states.prevData.denyList!} />
                  </Grid>
                  <Grid item xs={12}>
                    <DetectPatternEditor baseData={states.prevData.detectPatterns!} />
                  </Grid>
                  <Grid item xs={12}>
                    <KeywordRuleEditor prevData={states.prevData.keywordRules!} />
                  </Grid>
                  <Grid item xs={12}>
                    <ExclusionRuleEditor prevData={states.prevData.exclusionRules!} />
                  </Grid>
                </>
              }
              
            </Grid>
          </CardContent>
        </FileInfoTypeDataContextProvider>
      </SubdataAssembleContextProvider>
    </Card>
  )
}

export default InfoTypeEditor;

const emptyInfoTypeDto = Object.freeze({
  name: '',
  displayName: '',
  description: '',
  isEnabled: true,
  detectPatterns: Object.freeze([]) as any,
  denyList: Object.freeze([]) as any,
  keywordRules: Object.freeze([]) as any,
  exclusionRules: Object.freeze([]) as any,
} as InfoTypeResponseDto)

const DetectPatternEditor : FC<{
  baseData: DetectPattern[],
}> = ({baseData}) => {
  const [states, setStates] = useState({
    prevData: baseData,
    stage: {
      added: [] as DetectPattern[], // sparse array
      removed: new Set<number>(),
    },
    touched: false,
  })
  useEffect(() => {
    setStates(v => ({...v, prevData: baseData, stage: {added: [], removed:new Set()}, touched: false}))
  }, [baseData])
  const { registerAssembleFn, inform } = useSubdataAssembleContext<InfoTypeInput>()
  const { assemble, contextValue } = useSubdataAssembleController<{[k in string]: {origin: DetectPattern, newValue: DetectPattern|null}}>({ 
    getInformed: (key, value) => {
      // will not get informed
    }
  })
  useEffect(() => {
    return registerAssembleFn('detectPatterns', () => { return new Promise(async (resolve) => {
      const qExcludes = promiseWithResolvers<string[]>()
      setStates(v => { 
        qExcludes.resolve([...v.stage.removed].map(i => `r[${i}]`))
        return v 
      })
      const excludes = await qExcludes.promise
      const result = await assemble(excludes)
      if (result instanceof AssembleIssue) {
        resolve(new AssembleIssue('detectPatterns'))
      } else {
        const r = result;
        resolve([
          ...(r.a as any as any[] ?? []).flatMap(x => [x.newValue]).reverse(),
          ...(r.r as any as any[] ?? []).flatMap(x => [x.newValue])
        ])
      }
    }) })
  }, [registerAssembleFn, assemble])
  const actionBtn = (d: DetectPattern, i: number) => {
    const atAdded = states.stage.added.indexOf(d)
    const isRestore = states.stage.removed.has(i)
    const actionFn = () => setStates(v => {
      if (atAdded > -1) { // delete added
        delete v.stage.added[atAdded]
      } else if (isRestore) { // restore removed
        v.stage.removed.delete(i)
      } else { // to remove
        v.stage.removed.add(i)
      }
      setTimeout(() => {
        inform('detectPatterns', [
          ...[...v.stage.added].reverse(), // the added dto's are just placeholders, good enough for the inform() method here it don't care
          ...v.prevData.filter((d, i) => !v.stage.removed.has(i))
        ] as DetectPattern[])
      })
      return {...v, touched: true}
    })
    
    return (
      <Tooltip title={isRestore ? "Restore" : "Remove"}>
        <IconButton color="secondary" onClick={actionFn}>
          {isRestore ? <ReplayIcon /> : <ClearIcon />}
        </IconButton>
      </Tooltip>)
  }
  const classOf = (d: DetectPattern, i: number) => {
    return states.stage.removed.has(i) ? 'removed' : undefined
  }

  return (
    <SubdataAssembleContextProvider value={contextValue}>
      <Box
        sx={[EditorLineSx as any, {
          '& li.MuiListItem-root div.line-box': {
            flex: '1 1 auto',
            mr: 2,
            '& .MuiTextField-root:last-of-type': {
              flex: '1 1 auto'
            }
          },
        }]}
      >
        <Stack direction="row" alignItems="center" spacing={4} marginBottom={1}>
          <Typography variant="subtitle2">Pattern Detection</Typography>
          <Tooltip title="Add Pattern">
            <IconButton color="primary"
              onClick={() => setStates(v => {
                v.stage.added.push({ name: '', score: 0.05, regex: ''})
                setTimeout(() => {
                  inform('detectPatterns', [
                    ...[...v.stage.added].reverse(),
                    ...v.prevData.filter((d, i) => !v.stage.removed.has(i))
                  ] as DetectPattern[])
                })
                return {...v, touched: true}
              })} 
            >
              <AddIcon /></IconButton>
          </Tooltip>
        </Stack>
        <Alert severity="info" sx={{ mb: 1 }}>
          Each pattern match is designated with a matching score. If a piece of content matches multiple patterns, the highest score out of all matches is used.
        </Alert>
        <List disablePadding>
          {states.stage.added.map((d, i) =>(
            <DetectPatternLine className="added" key={i} r_key={`a[${i}]`} prevData={d} actionButton={actionBtn(d, i)}/>
          )).filter(d => !!d).reverse()}
        </List>
        <List disablePadding>
          {states.prevData.map((d, i) => (
            <DetectPatternLine className={classOf(d, i)} key={i} r_key={`r[${i}]`} prevData={d} actionButton={actionBtn(d, i)}/>
          ))}
        </List>
      </Box>
    </SubdataAssembleContextProvider>
  )
}

const dpSchema = Yup.object({
  name: Yup.string().required('Name is required').max(100, 'Name must be at most 100 characters'),
  score: Yup.number().required('Score is required'),
  regex: Yup.string().required('Regular Expression is required')
    .test('is-valid-regex', 'Regular Expression value is incorrect', (x:string) => {
      try {
        new RegExp(x)
        return true
      } catch {
        return false
      }
    }),
})

const DetectPatternLine : FC<{ 
  className?: string,
  r_key: string,
  prevData: DetectPattern, 
  actionButton: ReactElement,
}> = ({ className, r_key, prevData, actionButton }) => {
  const [states, setStates] = useState({
    prevData,
    stage: {...prevData},
    errors: {} as any,
    touched: {} as any,
  })
  useEffect(() => { setStates(v => ({...v, prevData, stage: {...prevData}, errors:{}, touched:{} })) }, [prevData])
  const { registerAssembleFn } = useSubdataAssembleContext<{[k in string]: {origin: DetectPattern, newValue: DetectPattern|null}}>()
  const { validate, fieldProps } = usePlainStateFormUtils(states, setStates, dpSchema)
  useEffect(() => {
    return registerAssembleFn(r_key, () => new Promise(resolve => {
      setStates(v => {
        const errors = validate(v.stage) ?? {}
        let ks = Object.keys(errors)
        if (ks.length > 0) {
          resolve(new AssembleIssue(errors[ks[0]]))
        } else {
          resolve({
            origin: v.prevData,
            newValue: (
              v.prevData.name === v.stage.name &&
              v.prevData.score === v.stage.score &&
              v.prevData.regex === v.stage.regex) ? v.prevData : v.stage 
          })
        }
        ks = Object.keys(v.stage)
        return {...v, errors, touched: ks.reduce((acc, k) => ({...acc, [k]:true}), {} as any) }
      })
    }))
  }, [r_key, registerAssembleFn, validate])
  const isDirty = (
    states.prevData.name !== states.stage.name ||
    states.prevData.score !== states.stage.score ||
    states.prevData.regex !== states.stage.regex)
  return (
    <ListItem className={className ?? (isDirty ? 'dirty' : undefined)}>
      <Stack className="line-box" direction="row" spacing={4} >
        <TextField label="Name"  variant="standard" size="small" {...fieldProps('name')} multiline/>
        <TextField variant="standard" size="small" type="number" sx={{ width: 60 }} 
          label={
            <Stack direction="row" alignItems="start">
              Score
              <Tooltip title="A matching score indicates how likely the matching content is actually of this Info Type. The score may be modified by the Context Rules below, if any. A final score of 0.5 or above is deemed that the matching content is of this Info Type.">
                <InfoIcon  sx={{ ml: 2, width: 16, cursor: 'pointer' }}/>
              </Tooltip>
            </Stack>} 
          {...fieldProps('score', (x: any) => {
            let n = +x
            if (Number.isNaN(n)) { return 0 }
            n = parseFloat(n.toFixed(2))
            return (
                n < 0 ? 0 
              : n > 1 ? 1 
              : n)
          })}
          inputProps={{ step: 0.01, max: 1, min: 0, }}
        />
        <TextField label="Regular Expression" variant="standard" size="small" {...fieldProps('regex')} multiline />
      </Stack>
      {actionButton}
    </ListItem>
  )
}

const EditorLineSx : SxProps<Theme> = {
  '& li.MuiListItem-root': {
    pl: 3,
    '&:hover': {
      bgcolor: theme => theme.palette.action.hover,
    },
    '&::before': {
      display: 'block',
      content: "''",
      position: 'absolute',
      left: 0,
      height: 'calc(100% - 2px)',
      width: 4,
      bgcolor: 'divider'
    }
  },
  '& li.MuiListItem-root.added::before': { bgcolor: 'success.light' },
  '& li.MuiListItem-root.removed': {
    '&::before': { bgcolor: 'error.light' },
    '& input, & textarea': { textDecoration: 'line-through' },
  },
  '& li.MuiListItem-root.dirty::before': { bgcolor: 'info.light' },
}

const DenyListEditor : FC<{
  prevData: string[]
}> = ({ prevData }) => {
  const [states, setStates] = useState({
    prevData,
    stage: [...prevData],
    dirty: false
  })
  useEffect(() => setStates({ prevData, stage: [...prevData], dirty: false }), [prevData])
  const { registerAssembleFn, inform } = useSubdataAssembleContext<InfoTypeInput>()
  useEffect(() => {
    return registerAssembleFn('denyList', () => new Promise((resolve) => {
      setStates(v => {
        resolve(v.stage)
        return v
      })
    }))
  }, [registerAssembleFn])
  return (
    <Box
      sx={[EditorLineSx]}
    >
      <Stack direction="row" marginBottom={1}>
        <Typography variant="subtitle2">Keyword Detection</Typography>
      </Stack>
      <Alert severity="info" sx={{ mb: 1}}>
        Any one of these keywords found (case insensitive) will be categorized as this Info Type.
      </Alert>
      <List disablePadding>
        <ListItem className={states.dirty ? 'dirty' : undefined}>
          <StringArrayTextField list={states.stage} stackChips="row" 
            setList={(ls) => setStates(v => {
              const stage = [...new Set(ls)]
              const dirty = !(v.prevData.length === stage.length && v.prevData.every(s => stage.indexOf(s) > -1))
              setTimeout(() => inform('denyList', stage))
              return {...v, stage, dirty}
            })}
            placeholder="Add a new value (case insensivite) and press the Enter key to confirm..."
          />
        </ListItem>
      </List>
    </Box>
  )
}

const KeywordRuleEditor : FC<{ prevData: KeywordRule[] }> = ({
  prevData,
}) => {
  const [states, setStates] = useState({
    prevData,
    stage: {
      added: [] as KeywordRule[], // sparse array
      removed: new Set<number>(),
    }
  })
  useEffect(() => {
    setStates(v => ({...v, prevData, stage: {added:[], removed: new Set()}}))
  }, [prevData])
  const { registerAssembleFn } = useSubdataAssembleContext<InfoTypeInput>()
  const { assemble, contextValue } = useSubdataAssembleController<{[k in string]: { origin:KeywordRule, newValue:KeywordRule|null }}>({
    getInformed: (key, value) => {
      // will not get informed
    }
  })
  useEffect(() => {
    return registerAssembleFn('keywordRules', () => new Promise(async resolve => {
      const qExcludes = promiseWithResolvers<string[]>()
      setStates(v => { 
        qExcludes.resolve([...v.stage.removed].map(i => `r[${i}]`))
        return v 
      })
      const excludes = await qExcludes.promise
      const result = await assemble(excludes)
      if (result instanceof AssembleIssue) {
        resolve(new AssembleIssue('keywordRules'))
      } else {
        const r = result
        resolve([
          ...(r.a as any as any[] ?? []).flatMap(x => [x.newValue]).reverse(),
          ...(r.r as any as any[] ?? []).flatMap(x => [x.newValue])
        ])
      }
    }))
  }, [registerAssembleFn, assemble])
  const actionBtn = (d: KeywordRule, i:number) => {
    const atAdded = states.stage.added.indexOf(d)
    const isRestore = states.stage.removed.has(i)
    const actionFn = () => setStates(v => {
      if (atAdded > -1) { // delete added
        delete v.stage.added[atAdded]
      } else if (isRestore) { // restore removed
        v.stage.removed.delete(i)
      } else { // to remove
        v.stage.removed.add(i)
      }
      return {...v}
    })
    
    return (
      <Tooltip title={isRestore ? "Restore" : "Remove"}>
        <IconButton color="secondary" onClick={actionFn}>
          {isRestore ? <ReplayIcon /> : <ClearIcon />}
        </IconButton>
      </Tooltip>)
  }
  const classOf = (d: KeywordRule, i: number) => {
    return states.stage.removed.has(i) ? 'removed' : undefined
  }
  return (
    <SubdataAssembleContextProvider value={contextValue}>
      <Box
        sx={[EditorLineSx]}
      >
        <Stack direction="row" alignItems="center" spacing={4} marginBottom={1}>
          <Typography variant="subtitle2">Context Rules</Typography>
          <Tooltip title="Add Context Rule">
            <IconButton color="primary"
              onClick={() => setStates(v => {
                v.stage.added.push({ words: [], regex: null, windowBefore: 100, windowAfter: 100, scoreAdjustment: 0.45})
                return {...v}
              })} 
            >
              <AddIcon /></IconButton>
          </Tooltip>
        </Stack>
        <Alert severity="info" sx={{ mb: 1 }}>
          A context rule is used to adjust the matching score derived from the Pattern Detection above. The context keywords or context pattern is searched within the Context Window specified below. If any match is found, the matching score derived from Pattern Detection is incremented by the Context Score. If matched by multiple context rules, the matching score is incremented by the highest context score.
        </Alert>
        <List disablePadding>
          {states.stage.added.map((d, i) => (
            <KeywordRuleLine className="added" key={i} r_key={`a[${i}]`} prevData={d} actionButton={actionBtn(d, i)} />
          )).filter(d => !!d).reverse()}
        </List>
        <List disablePadding>
          {states.prevData.map((d, i) => (
            <KeywordRuleLine className={classOf(d, i)} key={i} r_key={`r[${i}]`} prevData={d} actionButton={actionBtn(d, i)} />
          ))}
        </List>
      </Box>
    </SubdataAssembleContextProvider>
  )
}

const krSchema = Yup.object({
  regex: Yup.string().nullable().test('is-valid-regex', 'Invalid regular expression', (x:string) => {
    try {
      x && new RegExp(x)
      return true
    } catch {
      return false
    }
  }),
  words: Yup.array(Yup.string()).nullable(),
})

const KeywordRuleLine : FC<{ 
  className?: string,
  r_key: string,
  prevData: KeywordRule, 
  actionButton: ReactElement,
}> = ({ className, r_key, prevData, actionButton }) => {
  const [states, setStates] = useState({
    prevData,
    stage: {...prevData},
    errors: {} as any,
    touched: {} as any,
  })
  useEffect(() => setStates(v => ({ prevData, stage: {...prevData}, errors: {} as any, touched: {} as any, })), [prevData])
  const { validate, fieldProps } = usePlainStateFormUtils(states, setStates, krSchema)
  const { registerAssembleFn } = useSubdataAssembleContext<{[k in string]: { origin:KeywordRule, newValue:KeywordRule|null }}>()
  useEffect(() => {
    return registerAssembleFn(r_key, () => new Promise(resolve => {
      setStates(v => {
        const errors = validate(v.stage) ?? {}
        if (!errors['regex'] && !errors['words']) {
          const isUsingWords = v.stage.regex == null
          if (isUsingWords) {
            if (!v.stage.words || v.stage.words.length === 0) {
              errors['words'] = 'Context Keywords are required'
            }
          } else {
            if (!v.stage.regex) {
              errors['regex'] = 'Context Pattern is required'
            }
            v.stage.words = null
          }
        }
        let ks = Object.keys(errors)
        if (ks.length > 0) {
          resolve(new AssembleIssue(errors[ks[0]]))
        } else {
          resolve({
            origin: v.prevData,
            newValue: (!keywordRuleDirty(v)) ? v.prevData : v.stage 
          })
        }
        ks = Object.keys(v.stage)
        return {...v, errors, touched: ks.reduce((acc, k) => ({...acc, [k]:true}), {} as any) }
      })
    }))
  }, [r_key, registerAssembleFn, validate])
  const isDirty = keywordRuleDirty(states)
  const isUsingWords = states.stage.regex == null
  const radioValue = isUsingWords ? 'words' : 'regex'
  const radioHandleChange = (e:any, value:string) => {
    if (value === radioValue) { return }
    if (value === 'words') {
      setStates(v => {
        v.stage.regex = null
        v.stage.words = [...(v.prevData.words ??[])]
        delete v.errors['regex']
        delete v.touched['regex']
        return {...v}
      })
    } else {
      setStates(v => {
        v.stage.words = null
        v.stage.regex = v.prevData.regex ?? ''
        delete v.errors['words']
        delete v.touched['words']
        return {...v}
      })
    }
  }
  return (
    <ListItem className={className ?? (isDirty ? 'dirty' : undefined)}>
      <Stack direction="row" flexWrap="wrap" alignItems="center" width="100%" marginBottom={2}>
        <FormControl sx={{ mr: 'auto'}} size="small">
          <FormLabel>Type</FormLabel>
          <RadioGroup row value={radioValue} onChange={radioHandleChange}>
            <FormControlLabel value="words" label="Context Keywords" control={<Radio />} />
            <FormControlLabel value="regex" label="Context Pattern" control={<Radio />} />
          </RadioGroup>
        </FormControl>
        {actionButton}
        <Box width="100%" marginBottom={2}>
          {isUsingWords
            ? <StringArrayTextField list={states.stage.words ?? []} stackChips="row"    
                setList={(ls) => setStates(v => {
                  const words = [...new Set(ls)]
                  return ({...v, 
                    stage: {...v.stage, words},
                    touched: {...v.touched, words: true},
                    ...(words.length === 0 ? {errors: {...v.errors, words: 'Context Keywords are required'}} : (delete v.errors['words'], {errors: {...v.errors}}))
                  })
                })} 
                placeholder="Add a new value (case insensitive) and press the Enter key to confirm..."
                error={Boolean(states.touched['words'] && states.errors['words'])}
                helperText={states.touched['words'] && states.errors['words']}
              />
            : <TextField fullWidth multiline {...fieldProps('regex')} placeholder="Type Regular Expression in here..." />}
        </Box>
        <Box marginRight={8}>
          <FormControl>
            <FormLabel sx={{mb:0.5, display: 'flex', flexDirection: 'row', alignItems: 'start'}}>
              <span>Context Window</span>
              <Tooltip title="A context window designates the number of characters before and after the matched content discovered in Pattern Detection.">
                <InfoIcon  sx={{ ml: 2, width: 16, cursor: 'pointer' }}/>
              </Tooltip>
            </FormLabel>
            <FormGroup row>
              <TextField variant="standard" label="Before" sx={{ width: 80, mr: 2 }} size="small" type="number" {...fieldProps('windowBefore', (x: any) => {
                let n = +x
                if (Number.isNaN(n)) { return 0 }
                n = Math.floor(n)
                return (n < 0 ? 0 : n)
              })} />
              <TextField variant="standard" label="After" sx={{ width: 80 }} size="small" type="number" {...fieldProps('windowAfter', (x: any) => {
                let n = +x
                if (Number.isNaN(n)) { return 0 }
                n = Math.floor(n)
                return (n < 0 ? 0 : n)
              })} />
            </FormGroup>
          </FormControl>
        </Box>
        <TextField variant="standard" label="Context Score" sx={{ width: 140, alignSelf: 'end' }} type="number" 
          {...fieldProps('scoreAdjustment', (x: any) => {
            let n = +x
            if (Number.isNaN(n)) { return 0 }
            n = parseFloat(n.toFixed(2))
            return (
                n < 0 ? 0 
              : n > 1 ? 1 
              : n)
          })} 
          inputProps={{ step: 0.01, max: 1, min: 0, }}
        />
      </Stack>
    </ListItem>
  )
}

const keywordRuleDirty = (x: {prevData: KeywordRule; stage:KeywordRule}) => {
  return (
    x.prevData.regex !== x.stage.regex ||
    x.prevData.scoreAdjustment !== x.stage.scoreAdjustment ||
    x.prevData.windowBefore !== x.stage.windowBefore ||
    x.prevData.windowAfter !== x.stage.windowAfter ||
    !!x.prevData.words !== !!x.stage.words ||
    (!!x.prevData.words && !(
      x.prevData.words.length === x.stage.words!.length && x.prevData.words.every(w => x.stage.words!.indexOf(w) > -1)))
    )
}

const ExclusionRuleEditor : FC<{ prevData: ExclusionRule[] }> = ({
  prevData
}) => {
  const [states, setStates] = useState({
    prevData,
    stage: {
      added: [] as ExclusionRule[], // sparse array
      removed: new Set<number>(),
    }
  })
  useEffect(() => {
    setStates(v => ({...v, prevData, stage: {added:[], removed: new Set()}}))
  }, [prevData])
  const { registerAssembleFn } = useSubdataAssembleContext<InfoTypeInput>()
  const { assemble, contextValue } = useSubdataAssembleController<{[k in string]: { origin:ExclusionRule, newValue:ExclusionRule|null }}>({
    getInformed: (key, value) => {
      // will not get informed
    }
  })
  useEffect(() => {
    return registerAssembleFn('exclusionRules', () => new Promise(async resolve => {
      const qExcludes = promiseWithResolvers<string[]>()
      setStates(v => { 
        qExcludes.resolve([...v.stage.removed].map(i => `r[${i}]`))
        return v 
      })
      const excludes = await qExcludes.promise
      const result = await assemble(excludes)
      if (result instanceof AssembleIssue) {
        resolve(new AssembleIssue('exclusionRules'))
      } else {
        const r = result
        resolve([
          ...(r.a as any as any[] ?? []).flatMap(x => [x.newValue]).reverse(),
          ...(r.r as any as any[] ?? []).flatMap(x => [x.newValue])
        ])
      }
    }))
  }, [registerAssembleFn, assemble])
  const actionBtn = (d: KeywordRule, i:number) => {
    const atAdded = states.stage.added.indexOf(d)
    const isRestore = states.stage.removed.has(i)
    const actionFn = () => setStates(v => {
      if (atAdded > -1) { // delete added
        delete v.stage.added[atAdded]
      } else if (isRestore) { // restore removed
        v.stage.removed.delete(i)
      } else { // to remove
        v.stage.removed.add(i)
      }
      return {...v}
    })
    
    return (
      <Tooltip title={isRestore ? "Restore" : "Remove"}>
        <IconButton color="secondary" onClick={actionFn}>
          {isRestore ? <ReplayIcon /> : <ClearIcon />}
        </IconButton>
      </Tooltip>)
  }
  const classOf = (d: KeywordRule, i: number) => {
    return states.stage.removed.has(i) ? 'removed' : undefined
  }
  return (
    <SubdataAssembleContextProvider value={contextValue}>
      <Box
        sx={[EditorLineSx]}
      >
        <Stack direction="row" alignItems="center" spacing={4} marginBottom={1}>
          <Typography variant="subtitle2">Exclusion Rules</Typography>
          <Tooltip title="Add Exclusion Rule">
            <IconButton color="primary"
              onClick={() => setStates(v => {
                v.stage.added.push({ words: [], regex: null})
                return {...v}
              })} 
            >
              <AddIcon /></IconButton>
          </Tooltip>
        </Stack>
        <Alert severity="info" sx={{ mb: 1 }}>
          An exclusion rule is used to exclude from this info type any contents that match certain keywords, patterns, or other info types.
        </Alert>
        <List disablePadding>
          {states.stage.added.map((d, i) => (
            <ExclusionRuleLine className="added" key={i} r_key={`a[${i}]`} prevData={d} actionButton={actionBtn(d, i)} />
          )).filter(d => !!d).reverse()}
        </List>
        <List disablePadding>
          {states.prevData.map((d, i) => (
            <ExclusionRuleLine className={classOf(d, i)} key={i} r_key={`r[${i}]`} prevData={d} actionButton={actionBtn(d, i)} />
          ))}
        </List>
      </Box>
    </SubdataAssembleContextProvider>
  )
}

const ExclusionRuleLine : FC<{ 
  className?: string,
  r_key: string,
  prevData: ExclusionRule, 
  actionButton: ReactElement
}> = ({ className, r_key, prevData, actionButton }) => {
  const [states, setStates] = useState({
    prevData,
    stage: {...prevData},
    errors: {} as any,
    touched: {} as any,
    radioValue: !!prevData.words ? 'words' : (!!prevData.regex ? 'regex' : 'infoTypes')
  })
  useEffect(() => setStates(v => ({ prevData, stage: {...prevData}, errors: {} as any, touched: {} as any, radioValue: !!prevData.words ? 'words' : (!!prevData.regex ? 'regex' : 'infoTypes'), })), [prevData])
  const { validate, fieldProps } = usePlainStateFormUtils(states, setStates, krSchema)
  const { registerAssembleFn } = useSubdataAssembleContext<{[k in string]: { origin:ExclusionRule, newValue:ExclusionRule|null }}>()
  
  useEffect(() => {
    return registerAssembleFn(r_key, () => new Promise(resolve => {
      setStates(v => {
        const errors = validate(v.stage) ?? {}
        if (!errors['regex'] && !errors['words'] && !errors['excludeInfoTypes']) {
          if (v.radioValue === 'words') {
            if (!v.stage.words || v.stage.words.length === 0) {
              errors['words'] = 'Exclusion Keywords are required'
            }
          } else if (v.radioValue === 'regex') {
            if (!v.stage.regex) {
              errors['regex'] = 'Exclusion Pattern is required'
            }
            v.stage.words = null
          } else {
            if (!v.stage.excludeInfoTypes || v.stage.excludeInfoTypes.length === 0) {
              errors['excludeInfoTypes'] = 'Exclusion Info Types are required'
            }
          }
        }
        let ks = Object.keys(errors)
        if (ks.length > 0) {
          resolve(new AssembleIssue(errors[ks[0]]))
        } else {
          resolve({
            origin: v.prevData,
            newValue: (!exclusionRuleDirty(v)) ? v.prevData : v.stage 
          })
        }
        ks = Object.keys(v.stage)
        return {...v, errors, touched: ks.reduce((acc, k) => ({...acc, [k]:true}), {} as any) }
      })
    }))
  }, [r_key, registerAssembleFn, validate])
  const isDirty = exclusionRuleDirty(states)
  const { infoTypes } = useFileInfoTypeDataContext()
  const infoTypeOptions = infoTypes.map((o) => o.name ?? '')
  const radioHandleChange = (e:any, value:string) => {
    if (value === 'words') {
      setStates(v => {
        v.radioValue = value
        v.stage.regex = null
        v.stage.excludeInfoTypes = null
        v.stage.words = [...(v.prevData.words ??[])]
        delete v.errors['regex']
        delete v.touched['regex']
        delete v.errors['excludeInfoTypes']
        delete v.touched['excludeInfoTypes']
        return {...v}
      })
    } else if (value === "regex") {
      console.log(value)
      setStates(v => {
        v.radioValue = value
        v.stage.words = null
        v.stage.excludeInfoTypes = null
        v.stage.regex = v.prevData.regex ?? ''
        delete v.errors['words']
        delete v.touched['words']
        delete v.errors['excludeInfoTypes']
        delete v.touched['excludeInfoTypes']
        return {...v}
      })
    } else {
      setStates(v => {
        v.radioValue = value
        v.stage.words = null
        v.stage.regex = null
        v.stage.excludeInfoTypes = [...(v.prevData.excludeInfoTypes ??[])]
        delete v.errors['words']
        delete v.touched['words']
        delete v.errors['regex']
        delete v.touched['regex']
        return {...v}
      })
    }
  }
  return (
    <ListItem className={className ?? (isDirty ? 'dirty' : undefined)}>
      <Stack direction="row" flexWrap="wrap" alignItems="center" width="100%" marginBottom={2}>
        <FormControl sx={{ mr: 'auto'}} size="small">
          <FormLabel>Type</FormLabel>
          <RadioGroup row value={states.radioValue} onChange={radioHandleChange}>
            <FormControlLabel value="words" label="Exclusion Keywords" control={<Radio />} />
            <FormControlLabel value="regex" label="Exclusion Pattern" control={<Radio />} />
            <FormControlLabel value="infoTypes" label="Exclusion Info Types" control={<Radio />} />
          </RadioGroup>
        </FormControl>
        {actionButton}
        <Box width="100%" marginBottom={2}>
          {states.radioValue === 'words'
            ? <StringArrayTextField list={states.stage.words ?? []} stackChips="row"    
                setList={(ls) => setStates(v => {
                  const words = [...new Set(ls)]
                  return ({...v, 
                    stage: {...v.stage, words},
                    touched: {...v.touched, words: true},
                    ...(words.length === 0 ? {errors: {...v.errors, words: 'Exclusion Keywords are required'}} : (delete v.errors['words'], {errors: {...v.errors}}))
                  })
                })} 
                placeholder="Add a new value (case insensitive) and press the Enter key to confirm..."
                error={Boolean(states.touched['words'] && states.errors['words'])}
                helperText={states.touched['words'] && states.errors['words']}
              />
            : states.radioValue === 'regex' ?
               <TextField fullWidth multiline {...fieldProps('regex')}
               error={Boolean(states.touched['regex'] && states.errors['regex'])}
               helperText={states.touched['regex'] && states.errors['regex']}
                placeholder="Type Regular Expression in here..." />
            : <Autocomplete multiple options={infoTypeOptions}
                getOptionLabel={(option) => option ?? ''}
                value={(states.stage.excludeInfoTypes ?? []) as string[]}
                renderInput={((params) =>
                  <TextField 
                    label="Exclusion Info Types"
                    {...params} 
                    fullWidth
                    error={Boolean(states.touched['excludeInfoTypes'] && states.errors['excludeInfoTypes'])}
                    helperText={states.touched['excludeInfoTypes'] && states.errors['excludeInfoTypes']}
                    {...{
                      inputProps: {
                        ...params.inputProps,
                        'aria-label': 'Exclusion Info Type',
                      }
                    }}
                  />
                )}
                onChange={(e, value, reason) => 
                  setStates(v => {
                    const excludeInfoTypes = [...new Set(value)]
                    return ({...v,
                      stage: {...v.stage, excludeInfoTypes},
                      touched: {...v.touched, excludeInfoTypes: true},
                      ...(excludeInfoTypes.length === 0 ? {errors: {...v.errors, excludeInfoTypes: 'Exclusion Info Types are required'}} : (delete v.errors['excludeInfoTypes'], {errors: {...v.errors}}))
                    })
                  })
                }
              />
        }
        </Box>
      </Stack>
    </ListItem>
  )
}

const exclusionRuleDirty = (x: {prevData: ExclusionRule; stage:ExclusionRule}) => {
  return (
    x.prevData.regex !== x.stage.regex ||
    !!x.prevData.words !== !!x.stage.words ||
    (!!x.prevData.words && !(
      x.prevData.words.length === x.stage.words!.length && x.prevData.words.every(w => x.stage.words!.indexOf(w) > -1))
    ) || !!x.prevData.excludeInfoTypes !== !!x.stage.excludeInfoTypes ||
      (!!x.prevData.excludeInfoTypes && !(
        x.prevData.excludeInfoTypes.length === x.stage.excludeInfoTypes!.length && x.prevData.excludeInfoTypes.every(w => x.stage.excludeInfoTypes!.indexOf(w) > -1)))
    ) 
}
